import PageHeader from "../components/PageHeader";
import {
  FaHotel,
  FaPlane,
  FaCar,
  FaMapMarkedAlt,
  FaHeadset,
  FaShieldAlt,
  FaUtensils,
  FaPassport
} from "react-icons/fa";

import { Helmet } from "react-helmet-async";

export default function Services() {

  const services = [
    {
      icon: <FaHotel />,
      title: "Hotel Booking Worldwide",
      desc: "Access thousands of hotels worldwide with competitive rates, instant confirmation, and flexible booking options.",
      link: "/hotels",
    },
    {
      icon: <FaPlane />,
      title: "Flight Booking",
      desc: "Book domestic and international flights with exclusive fares, flexible schedules, and trusted airline partners.",
      link: "/flights",
    },
    {
      icon: <FaPassport />,
      title: "Visa Assistance",
      desc: "Professional visa support and documentation assistance for tourist, business, and transit travel requirements.",
      link: "/visa-services",
    },
    {
      icon: <FaMapMarkedAlt />,
      title: "Tours & Activities",
      desc: "Discover unforgettable sightseeing experiences, city tours, desert safaris, attractions, and adventure activities.",
      link: "/tours",
    },
    {
      icon: <FaCar />,
      title: "Car Rentals",
      desc: "Choose from economy to luxury vehicles with flexible rental plans and convenient pickup options.",
      link: "/car-rental",
    },
    {
      icon: <FaShieldAlt />,
      title: "Airport Transfers",
      desc: "Reliable private and shared airport transfer services ensuring a smooth journey from arrival to destination.",
      link: "/airport-transfers",
    },
    {
      icon: <FaHeadset />,
      title: "Cruise Booking",
      desc: "Explore premium cruise vacations featuring luxury accommodations, entertainment, and world-class destinations.",
      link: "/cruises",
    },
    {
      icon: <FaMapMarkedAlt />,
      title: "Holiday Packages",
      desc: "Carefully curated holiday packages combining flights, hotels, transfers, and experiences at exceptional value.",
      link: "/holiday-packages",
    },
  ];

  return (
    <>
      <Helmet>
        {/* Basic SEO */}
        <title>Our Services | UAE Discounted Hotels</title>

        <meta
          name="description"
          content="Explore the services offered by UAE Discounted Hotels, including discounted hotel bookings, luxury stays, budget accommodations, family hotels, business travel, and exclusive hotel deals across the UAE."
        />

        <meta
          name="keywords"
          content="UAE hotel services, hotel booking UAE, discounted hotels UAE, luxury hotels, budget hotels, family hotels, business hotels, hotel deals Dubai, Abu Dhabi hotels"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="UAE Discounted Hotels" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://uaediscountedhotels.com/services"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="UAE Discounted Hotels" />
        <meta property="og:locale" content="en_US" />

        <meta
          property="og:title"
          content="Our Services | UAE Discounted Hotels"
        />

        <meta
          property="og:description"
          content="Discover our hotel booking services, exclusive hotel discounts, and accommodation options across Dubai, Abu Dhabi, Sharjah, and the UAE."
        />

        <meta
          property="og:url"
          content="https://uaediscountedhotels.com/services"
        />

        <meta
          property="og:image"
          content="https://uaediscountedhotels.com/images/default-og.png"
        />

        {/* Twitter */}
        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content="Our Services | UAE Discounted Hotels"
        />

        <meta
          name="twitter:description"
          content="Learn about the hotel booking services and exclusive accommodation deals offered by UAE Discounted Hotels."
        />

        <meta
          name="twitter:image"
          content="https://uaediscountedhotels.com/images/default-og.png"
        />
      </Helmet>

      <div className="w-full bg-gray-50 dark:bg-gray-950">

        <PageHeader
          title="Our Services"
          subtitle="Everything you need for a seamless travel experience"
        />

        {/* ================= SERVICES ================= */}
        <section className="max-w-6xl mx-auto px-4 py-14 space-y-6">

          {services.map((s, i) => (
            <a
              key={i}
              href={s.link}
              target="_blank"
              className="
        group flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6

        bg-white dark:bg-gray-900
        border border-gray-200 dark:border-gray-800

        rounded-2xl p-5 sm:p-6

        shadow-[0_10px_30px_rgba(0,0,0,0.08)]
        dark:shadow-[0_10px_30px_rgba(0,0,0,0.4)]

        hover:shadow-[0_25px_60px_rgba(0,0,0,0.18)]
        dark:hover:shadow-[0_25px_70px_rgba(0,0,0,0.6)]

        transition-all duration-300
        hover:-translate-y-1

        relative overflow-hidden
      "
            >

              {/* ICON */}
              <div className="
        w-14 h-14 sm:w-16 sm:h-16
        flex items-center justify-center
        rounded-2xl text-white text-xl
        bg-gradient-to-r from-blue-600 via-cyan-500 to-blue-700
        dark:from-gray-800 dark:via-gray-700 dark:to-gray-900
        shrink-0
        mx-auto sm:mx-0
        group-hover:rotate-6 transition
      ">
                {s.icon}
              </div>

              {/* CONTENT */}
              <div className="flex-1 text-center sm:text-left">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {s.title}
                </h3>

                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                  {s.desc}
                </p>
              </div>

              {/* BUTTON */}
              <div className="
        px-4 py-2
        rounded-lg
        bg-blue-600 text-white
        dark:bg-cyan-500 dark:text-gray-900

        text-sm font-semibold

        opacity-90 group-hover:opacity-100
        group-hover:scale-105

        transition
        whitespace-nowrap

        w-full sm:w-auto
        text-center
      ">
                Explore
              </div>

              {/* HOVER BACKGROUND */}
              <div className="
        absolute inset-0
        bg-gradient-to-r from-blue-50 to-cyan-50
        dark:from-gray-800 dark:to-gray-900
        opacity-0 group-hover:opacity-100
        transition
        -z-10
      " />

            </a>
          ))}

        </section>

      </div>
    </>
  );
}